import { CIMSModal } from 'components/CIMSModal'
import { useIssueTrackerContext } from '../../IssueTrackerContext'
import Title from './components/Title'
import { IssueStatus } from '@cims-online/shared'
import {
  CloseIssueInput,
  GiveIssueDispositionInput,
  RespondToIssueInput,
  useActionModalMetadataQuery,
  useCloseIssueMutation,
  useGiveIssueDispositionMutation,
  useRespondToIssueMutation,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useState } from 'react'
import { ActionModalValues } from './types'
import CommonFormItemValues from './components/CommonFormItems'
import PendingResponseFormItems from './components/PendingResponseFormItems'
import GiveDispositionFormItems from './components/GiveDispositionFormItems'
import ClosureFormItems from './components/ClosureFormItems'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useProjectContext } from 'context/ProjectContext'

const ActionModal: React.VFC = () => {
  const { projectId } = useProjectContext()
  const {
    actionModal: { state, toggle, visible },
    issues,
    relations,
  } = useIssueTrackerContext()
  const [respond] = useRespondToIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      respondToIssue: {
        issue: { formattedNumber },
      },
    }) =>
      simpleSuccessNotification(`Sucessfully responded to ${formattedNumber}!`),
  })
  const [give] = useGiveIssueDispositionMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      giveIssueDisposition: {
        issue: { formattedNumber },
      },
    }) =>
      simpleSuccessNotification(
        `Successfully gave ${formattedNumber} a disposition!`
      ),
  })
  const [close] = useCloseIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      closeIssue: {
        issue: { formattedNumber },
      },
    }) => simpleSuccessNotification(`Successfully closed ${formattedNumber}!`),
  })
  const [mutating, setMutating] = useState(false)
  const { data, loading } = useActionModalMetadataQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  const options = useOptions({
    items: data?.issueDispositionTypes?.records?.filter(
      ({ nonApplicableIssueTypes }) => {
        const typeIds = (state?.types ?? []).map(({ id }) => id)
        return nonApplicableIssueTypes.every(({ id }) => !typeIds.includes(id))
      }
    ),
    sort: 'name',
    label: 'name',
    value: 'id',
    tooltip: 'description',
  })
  if (!state) return null
  return (
    <CIMSModal
      title={<Title {...state} />}
      visible={visible}
      onCancel={() => toggle(null)}
      loading={mutating}
      initialFormValues={
        {
          /**
           * ignore fields here on purpose so they can be correctly initialized
           * in each conditional component
           */
          response: state.response,
          externalLinks: state.externalLinks,
        } as unknown as ActionModalValues
      }
      onOk={async ({ comment, externalLinks, ...values }) => {
        setMutating(true)
        const input: Pick<
          RespondToIssueInput & GiveIssueDispositionInput & CloseIssueInput,
          'id' | 'externalLinks' | 'comment'
        > = {
          id: state.id,
          externalLinks: externalLinks?.map(({ href, label }) => ({
            href,
            label,
          })),
          comment,
        }
        if (values.action === 'respond') {
          const { response } = values
          await respond({ variables: { input: { ...input, response } } })
        } else if (values.action === 'give') {
          const { dispositionType: dispitionType, dispositionNotes } = values
          await give({
            variables: {
              input: {
                ...input,
                dispositionNotes,
                dispositionTypeId: dispitionType.value.toString(),
              },
            },
          })
        } else if (values.action === 'close') {
          const { closureNotes } = values
          await close({ variables: { input: { ...input, closureNotes } } })
        }
        issues.refetch()
        if (comment) relations.refetch()
        setMutating(false)
        toggle(null)
      }}
    >
      {state.statusId === IssueStatus.PendingResponse.id && (
        <PendingResponseFormItems />
      )}
      {state.statusId === IssueStatus.PendingDisposition.id && (
        <GiveDispositionFormItems loading={loading} options={options} />
      )}
      {state.statusId === IssueStatus.PendingClosure.id && <ClosureFormItems />}
      <CommonFormItemValues {...state} />
    </CIMSModal>
  )
}

export default ActionModal
